import {
    type MRT_Column,
    type MRT_ColumnDef,
    type MRT_ColumnOrderState,
    type MRT_DefinedColumnDef,
    type MRT_DefinedTableOptions,
    type MRT_FilterOption,
    type MRT_RowData,
} from '../types';

export const getColumnId = <TData extends MRT_RowData>(columnDef: MRT_ColumnDef<TData>): string =>
    columnDef.id ?? columnDef.accessorKey?.toString?.() ?? columnDef.header;

export const getAllLeafColumnDefs = <TData extends MRT_RowData>(
    columns: MRT_ColumnDef<TData>[],
): MRT_ColumnDef<TData>[] => {
    const allLeafColumnDefs: MRT_ColumnDef<TData>[] = [];
    const getLeafColumns = (cols: MRT_ColumnDef<TData>[]) => {
        cols.forEach((col) => {
            if (col.columns) {
                getLeafColumns(col.columns);
            } else {
                allLeafColumnDefs.push(col);
            }
        });
    };
    getLeafColumns(columns);
    return allLeafColumnDefs;
};

/** Maps persisted v8 pinning (`left`/`right`) and resizing (`columnSizingInfo`) onto v9 keys. */
export const migrateV8MRTState = <T extends Record<string, any>>(state?: null | T): T | undefined => {
    if (!state) return state ?? undefined;
    const next: Record<string, any> = { ...state };
    const pinning = next.columnPinning as
        | { end?: string[]; left?: string[]; right?: string[]; start?: string[] }
        | undefined;
    if (pinning && pinning.start === undefined && pinning.end === undefined && (pinning.left || pinning.right)) {
        next.columnPinning = {
            start: pinning.left ?? [],
            end: pinning.right ?? [],
        };
    }
    if (next.columnSizingInfo && !next.columnResizing) {
        next.columnResizing = next.columnSizingInfo;
        delete next.columnSizingInfo;
    }
    return next as T;
};

export const prepareColumns = <TData extends MRT_RowData>({
    columnDefs,
    tableOptions,
}: {
    columnDefs: MRT_ColumnDef<TData>[];
    tableOptions: MRT_DefinedTableOptions<TData>;
}): MRT_DefinedColumnDef<TData>[] => {
    const {
        defaultDisplayColumn,
        filterFns = {},
        sortFns = {},
        sortingFns = {},
        state: { columnFilterFns = {} } = {},
    } = tableOptions;
    return columnDefs.map((columnDef) => {
        //assign columnId
        if (!columnDef.id) columnDef.id = getColumnId(columnDef);
        //assign columnDefType
        if (!columnDef.columnDefType) columnDef.columnDefType = 'data';
        if (columnDef.columns?.length) {
            columnDef.columnDefType = 'group';
            //recursively prepare columns if this is a group column
            columnDef.columns = prepareColumns({
                columnDefs: columnDef.columns,
                tableOptions,
            });
        } else if (columnDef.columnDefType === 'data') {
            //assign filterFns
            if (Object.keys(filterFns).includes(columnFilterFns[columnDef.id])) {
                columnDef.filterFn =
                    (filterFns as Record<string, MRT_DefinedColumnDef<TData>['filterFn']>)[
                        columnFilterFns[columnDef.id]
                    ] ?? (filterFns as Record<string, MRT_DefinedColumnDef<TData>['filterFn']>).fuzzy;
                (columnDef as MRT_DefinedColumnDef<TData>)._filterFn = columnFilterFns[columnDef.id];
            }

            const sortFnName = (columnDef.sortFn ?? (columnDef as { sortingFn?: string }).sortingFn) as string;
            const resolvedSortFns = { ...sortingFns, ...sortFns };
            //assign sortFns
            if (Object.keys(resolvedSortFns).includes(sortFnName)) {
                // @ts-ignore
                columnDef.sortFn = resolvedSortFns[sortFnName];
            }
        } else if (columnDef.columnDefType === 'display') {
            columnDef = {
                ...(defaultDisplayColumn as MRT_ColumnDef<TData>),
                ...columnDef,
            };
        }
        return columnDef;
    }) as MRT_DefinedColumnDef<TData>[];
};

export const reorderColumn = <TData extends MRT_RowData>(
    draggedColumn: MRT_Column<TData>,
    targetColumn: MRT_Column<TData>,
    columnOrder: MRT_ColumnOrderState,
): MRT_ColumnOrderState => {
    if (draggedColumn.getCanPin()) {
        draggedColumn.pin(targetColumn.getIsPinned());
    }
    const newColumnOrder = [...columnOrder];
    newColumnOrder.splice(
        newColumnOrder.indexOf(targetColumn.id),
        0,
        newColumnOrder.splice(newColumnOrder.indexOf(draggedColumn.id), 1)[0],
    );
    return newColumnOrder;
};

export const getDefaultColumnFilterFn = <TData extends MRT_RowData>(
    columnDef: MRT_ColumnDef<TData>,
): MRT_FilterOption => {
    const { filterVariant } = columnDef;
    if (filterVariant === 'multi-select') return 'arrIncludesSome';
    if (filterVariant?.includes('range')) return 'betweenInclusive';
    if (['checkbox', 'date', 'select'].includes(filterVariant || '')) return 'equals';
    return 'fuzzy';
};
